import React, { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import firebaseErrorMessage from '../../../utils/firebaseErrorMessage';
import { VolugraphMiniGaugeSpinner } from '../../../components/VolugraphMiniGaugeSpinner.js';

const RANGE_OPTIONS = [
  { label: 'Last 5',  value: 5 },
  { label: 'Last 10', value: 10 },
  { label: 'Last 20', value: 20 },
  { label: 'Last 30', value: 30 },
  { label: 'All',     value: 100 },
];

function formatInvoiceDate(ts) {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatAmount(amount, currency) {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${(currency || 'USD').toUpperCase()}`;
  }
}

const InvoicesSection = ({ hasStripeCustomer }) => {
  const [range, setRange] = useState(5);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  const fetchInvoices = useCallback(async (limit) => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'listStripeInvoices');
      const { data } = await fn({ limit });
      setInvoices(data.invoices || []);
    } catch (err) {
      setError(firebaseErrorMessage(err, 'Could not load invoices'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasStripeCustomer) return;
    fetchInvoices(range);
  }, [hasStripeCustomer, range, fetchInvoices]);

  useEffect(() => {
    const handle = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [dropdownOpen]);

  if (!hasStripeCustomer) return null;

  const selectedLabel = RANGE_OPTIONS.find((o) => o.value === range)?.label || 'Last 5';

  return (
    <section className="studio-billing-invoices">
      <div className="studio-billing-invoices-header">
        <h2 className="studio-billing-invoices-title">Invoices</h2>
        <div className="studio-billing-invoices-range-wrap" ref={dropdownRef}>
          <button
            type="button"
            className="studio-billing-invoices-range-btn"
            onClick={() => setDropdownOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            aria-label="Select invoice range"
          >
            {selectedLabel}
            <span className="studio-billing-invoices-range-chevron" aria-hidden="true" />
          </button>
          {dropdownOpen && (
            <ul
              className="studio-billing-invoices-range-list"
              role="listbox"
              aria-label="Invoice range options"
            >
              {RANGE_OPTIONS.map((opt) => (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === range}
                  className={`studio-billing-invoices-range-option${opt.value === range ? ' is-selected' : ''}`}
                  onClick={() => {
                    setRange(opt.value);
                    setDropdownOpen(false);
                  }}
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="studio-billing-invoices-body">
        {loading && (
          <div className="studio-billing-invoices-loading">
            <VolugraphMiniGaugeSpinner />
            <span>Loading invoices…</span>
          </div>
        )}
        {!loading && error && (
          <p className="studio-billing-invoices-error">{error}</p>
        )}
        {!loading && !error && invoices && invoices.length === 0 && (
          <p className="studio-billing-invoices-empty">No invoices found.</p>
        )}
        {!loading && !error && invoices && invoices.length > 0 && (
          <ul className="studio-billing-invoices-list">
            {invoices.map((inv) => (
              <li key={inv.id} className="studio-billing-invoices-row">
                <span className="studio-billing-invoices-row-name">
                  {inv.number || inv.id}
                </span>
                <span className="studio-billing-invoices-row-date">
                  {formatInvoiceDate(inv.created)}
                </span>
                <span className="studio-billing-invoices-row-amount">
                  {formatAmount(inv.total, inv.currency)}
                </span>
                {inv.invoice_pdf ? (
                  <a
                    href={inv.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="studio-billing-invoices-row-download"
                    aria-label={`Download invoice ${inv.number || inv.id} as PDF`}
                  >
                    <i className="ph ph-download-simple" aria-hidden="true" />
                    <span>PDF</span>
                  </a>
                ) : (
                  <span className="studio-billing-invoices-row-no-pdf">—</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default InvoicesSection;
